import { PageHero } from '@components/common';
import { getSectorCard } from '@data/sectorCards';
import { CALENDLY_URL } from '@constants';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, Zap, Factory, Globe } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function EnergyPage() {
  const sectorSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Fractional CISO for Energy & Utilities',
    description:
      'Cybersecurity leadership for critical infrastructure navigating NIS2, OT/ICS security, and smart grid protection',
    url: 'https://thecisonetwork.com/sectors/energy',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
    areaServed: {
      '@type': 'DefinedTerm',
      name: 'Energy & Utilities',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(sectorSchema)}</script>
      <PageHero
        title="Fractional CISO for Energy & Utilities"
        subtitle="Cybersecurity leadership for critical infrastructure navigating NIS2, OT/ICS security, and smart grid protection"
        eyebrow="Energy & Utilities"
        backgroundImage={getSectorCard('energy')?.image}
        backgroundAlt={getSectorCard('energy')?.imageAlt}
        seoTitle="Fractional CISO Energy & Utilities | The CISO Network"
        seoDescription="Expert fractional CISO services for energy and utilities. Protect critical infrastructure, achieve NIS2 compliance, and secure OT/ICS environments."
      />

      {/* Why Energy Needs CISOs */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Cybersecurity Leadership for Critical Infrastructure
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Energy and utilities companies operate the most critical infrastructure in any
                  nation. Power grids, water treatment facilities, and gas distribution networks are
                  essential to daily life and economic function. When they fail, the consequences
                  extend far beyond financial losses - they affect public safety, healthcare,
                  transportation, and national security.
                </p>
                <p>
                  The sector faces a perfect storm of cybersecurity challenges. Legacy operational
                  technology (OT) systems, some decades old, were designed for reliability and
                  availability - not security. These systems are increasingly connected to IT
                  networks and the internet, creating pathways for attackers to move from corporate
                  networks to critical control systems.
                </p>
                <p>
                  Regulatory pressure is intensifying. The{' '}
                  <Link to="/services/compliance" className="text-secondary hover:underline">
                    NIS2 Directive
                  </Link>{' '}
                  substantially expands cybersecurity obligations for energy companies across the EU
                  and UK. Critical infrastructure operators face mandatory incident reporting,
                  security audits, and significant penalties for non-compliance. The UK NCSC
                  provides guidance but meeting it requires dedicated security leadership.
                </p>
                <p>
                  Our fractional CISOs bring critical infrastructure expertise to energy and
                  utilities companies. We understand the unique challenges of OT/ICS environments,
                  the need to maintain operations while improving security, and the regulatory
                  landscape you must navigate.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Factory className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      OT/ICS Security Expertise
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Bridge the gap between IT and operational technology security. Our CISOs
                      understand both worlds and can secure legacy industrial systems without
                      disrupting operations.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Globe className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      NIS2 & Regulatory Compliance
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Navigate NIS2 requirements, UK NIS Regulations, and sector-specific
                      obligations. Build compliance programmes that satisfy regulators while
                      actually improving security.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Historical Security Cases */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              When Energy Security Fails
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              High-profile attacks on critical energy infrastructure worldwide
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: AlertTriangle,
                title: 'Colonial Pipeline (2021)',
                impact: '$4.4M ransom paid, East Coast fuel shortage',
                description:
                  'Ransomware on IT systems forced shutdown of the largest US fuel pipeline. A CISO would implement network segmentation between IT and OT, robust backup strategies, and incident response plans to maintain operations during attacks.',
              },
              {
                icon: AlertTriangle,
                title: 'Ukraine Power Grid (2015)',
                impact: '230,000 customers without power for 6 hours',
                description:
                  'First known successful cyberattack on a power grid. Attackers used spear phishing to access control systems. A CISO would enforce multi-factor authentication, network monitoring, and employee security training.',
              },
              {
                icon: AlertTriangle,
                title: 'Oldsmar Water (2021)',
                impact: 'Attempted water supply poisoning',
                description:
                  'Attacker gained access to water treatment controls and attempted to increase lye levels. A CISO would implement strong access controls, network segmentation, and anomaly detection for critical control changes.',
              },
              {
                icon: AlertTriangle,
                title: 'Saudi Aramco (2012)',
                impact: '35,000 computers destroyed, $15M+ damage',
                description:
                  'Shamoon malware wiped data from thousands of computers. A CISO would implement endpoint protection, network segmentation, and data backup strategies to contain and recover from destructive attacks.',
              },
              {
                icon: AlertTriangle,
                title: 'German Steel Mill (2014)',
                impact: 'Physical damage to blast furnace',
                description:
                  'Attackers manipulated control systems causing massive physical damage. A CISO would ensure OT systems have proper monitoring, access controls, and are isolated from IT networks to prevent such attacks.',
              },
              {
                icon: AlertTriangle,
                title: 'US SolarWinds (2020)',
                impact: 'Multiple energy sector victims compromised',
                description:
                  'Supply chain attack compromised numerous energy companies. A CISO would establish vendor risk management, software supply chain security, and threat detection for advanced persistent threats.',
              },
            ].map((case_, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-border">
                <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                  <case_.icon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {case_.title}
                </h3>
                <p className="text-sm font-medium text-red-600 mb-3">{case_.impact}</p>
                <p className="text-muted-foreground text-sm">{case_.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Risks */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Critical Risks Facing Energy & Utilities
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              The threats to critical infrastructure and how they affect your operations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Zap,
                title: 'Ransomware & Extortion',
                description:
                  'Critical infrastructure is prime ransomware target. Attackers know downtime costs millions and safety is at risk, making you more likely to pay.',
              },
              {
                icon: Factory,
                title: 'OT/IT Convergence',
                description:
                  'Connecting legacy operational systems to modern networks creates attack paths. Most OT systems were never designed with security in mind.',
              },
              {
                icon: Shield,
                title: 'Nation-State Threats',
                description:
                  'Critical infrastructure is a target for state-sponsored actors. APT groups conduct reconnaissance and maintain persistent access for future disruption.',
              },
              {
                icon: Globe,
                title: 'NIS2 Compliance',
                description:
                  'NIS2 expands security obligations and increases penalties. Energy companies face mandatory reporting, audits, and potential substantial fines.',
              },
            ].map((risk, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <risk.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {risk.title}
                </h3>
                <p className="text-muted-foreground text-sm">{risk.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO for Energy - FAQs"
        subtitle="Common questions from energy, utilities, and critical infrastructure organisations"
        items={[
          {
            id: 'energy-1',
            question: 'How does a fractional CISO work with energy companies?',
            answer:
              'Our CISOs work with energy generators, distributors, and critical infrastructure operators. They understand the unique requirements of OT/IT convergence and provide governance that protects industrial control systems while maintaining operational availability.',
          },
          {
            id: 'energy-2',
            question: 'What regulations apply to cybersecurity in the energy sector?',
            answer:
              'Energy companies face NIS Regulations, the Cyber Security and Resilience Bill, Ofgem requirements, and potentially NIS2 if operating in Europe. OT security must address IEC 62443 standards. Our CISOs have direct experience navigating this complex regulatory landscape and building operational resilience programmes that satisfy regulators.',
          },
          {
            id: 'energy-3',
            question: 'How does a fractional CISO handle OT/IT convergence?',
            answer:
              'Our CISOs specialise in the intersection of operational technology and IT security. We help energy companies build unified security governance that respects the availability requirements of industrial control systems while applying modern cybersecurity controls.',
          },
          {
            id: 'energy-4',
            question: 'What is the first priority for an energy sector fractional CISO?',
            answer:
              'Understanding the operational technology environment and its criticality to energy delivery. Our CISOs begin by mapping OT assets, identifying crown jewels, and assessing pathways between IT and OT networks. This ensures security improvements never compromise operational safety.',
          },
          {
            id: 'energy-5',
            question: 'Can a fractional CISO help with NIS compliance?',
            answer:
              'Yes. We have helped numerous energy companies achieve and maintain NIS compliance, prepare for the expanded obligations of the Cyber Security and Resilience Bill, and engage effectively with sector regulators. Our CISOs understand the specific compliance expectations for UK energy operators.',
          },
        ]}
      />

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Protect Your Critical Infrastructure
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            The consequences of a security breach extend far beyond financial loss. Our fractional
            CISOs help you protect your operations, achieve NIS2 compliance, and secure your OT
            environments.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
